import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  Switch,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Input,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { mapPartSearchHit } from "@svan-garaaz/reactsharedlib";
import Services, { type TestCustomerOption, type TestSupplierOption } from "../services";
import OrderServices from "../../Orders/services";
import { GARAAZ_PERSONNEL_SUB_TYPES } from "../../../type";
import type { TesterConfig, TesterPart } from "./types";
import { DEFAULT_TESTER_CONFIG } from "./types";
import { applyCustomerToConfig, applySupplierToConfig, type TesterEnvironment } from "./useTesterDefaults";
import useNotification from "../../../utils/useNotification";

type SavedRider = { id: string; name: string; phone: string };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  config: TesterConfig;
  env: TesterEnvironment | undefined;
  onSave: (config: TesterConfig) => void;
}

const useDebounced = (value: string, delay = 350) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const Section: React.FC<{
  title: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, hint, action, children }) => (
  <Box>
    <HStack justify="space-between" align="center">
      <Text fontSize="sm" fontWeight="700" color="gray.700" textTransform="uppercase" letterSpacing="0.04em">
        {title}
      </Text>
      {action}
    </HStack>
    {hint && (
      <Text fontSize="xs" color="gray.500" mb={2}>
        {hint}
      </Text>
    )}
    <Box mt={2}>{children}</Box>
  </Box>
);

const EditConfigModal: React.FC<Props> = ({ isOpen, onClose, config, env, onSave }) => {
  const toast = useNotification();
  const [draft, setDraft] = useState<TesterConfig>(config);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerSearchHits, setCustomerSearchHits] = useState<TestCustomerOption[]>([]);
  const [showAllCustomers, setShowAllCustomers] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerDetail, setCustomerDetail] = useState<TestCustomerOption | null>(null);
  const [partQuery, setPartQuery] = useState("");
  const [partResults, setPartResults] = useState<ReturnType<typeof mapPartSearchHit>[]>([]);
  const [partLoading, setPartLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [carMakers, setCarMakers] = useState<string[]>([]);
  const [savedRiders, setSavedRiders] = useState<SavedRider[]>([]);

  const debouncedCustomer = useDebounced(customerQuery);
  const debouncedPart = useDebounced(partQuery);

  useEffect(() => {
    if (isOpen) {
      setDraft(config);
      setCustomerQuery("");
      setCustomerSearchHits([]);
      setShowAllCustomers(false);
      setPartQuery("");
      setPartResults([]);
    }
  }, [isOpen, config]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    OrderServices.getGrazeons({ userSubType: GARAAZ_PERSONNEL_SUB_TYPES.USTAAD })
      .then((res: any) => {
        if (cancelled) return;
        const list: SavedRider[] = Array.isArray(res?.data)
          ? res.data
              .map((item: any) => {
                const user = item?.user || {};
                return {
                  id: String(user._id || ""),
                  name: [user.firstName, user.lastName].filter(Boolean).join(" "),
                  phone: user.mobileNumber || "",
                };
              })
              .filter((r: SavedRider) => r.id)
          : [];
        setSavedRiders(list);
      })
      .catch(() => {
        if (!cancelled) setSavedRiders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    Services.getCarMakers()
      .then((data: any) => {
        const list = Array.isArray(data) ? data : data?.makers || [];
        setCarMakers(
          list.map((m: any) => m?.name || m?.makerName || String(m)).filter(Boolean)
        );
      })
      .catch(() => setCarMakers([]));
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !draft.customerId) {
      setCustomerDetail(null);
      return;
    }
    Services.getTestCustomerById(draft.customerId)
      .then(setCustomerDetail)
      .catch(() => setCustomerDetail(null));
  }, [isOpen, draft.customerId]);

  useEffect(() => {
    if (debouncedCustomer.trim().length < 2) {
      setCustomerSearchHits([]);
      return;
    }
    let cancelled = false;
    setCustomerLoading(true);
    Services.searchTestCustomers(debouncedCustomer)
      .then((res) => !cancelled && setCustomerSearchHits(res))
      .catch(() => !cancelled && setCustomerSearchHits([]))
      .finally(() => !cancelled && setCustomerLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debouncedCustomer]);

  // Test customers only unless "Show all customers" is ticked.
  const customerResults = useMemo(
    () => (showAllCustomers ? customerSearchHits : customerSearchHits.filter((c) => c.isTestCustomer)),
    [customerSearchHits, showAllCustomers]
  );
  const hiddenCustomerCount = customerSearchHits.length - customerResults.length;

  useEffect(() => {
    if (debouncedPart.trim().length < 2) {
      setPartResults([]);
      return;
    }
    let cancelled = false;
    setPartLoading(true);
    Services.searchParts(debouncedPart, draft.carMaker || undefined)
      .then((items: unknown[]) => {
        if (cancelled) return;
        setPartResults(items.map(mapPartSearchHit).filter(Boolean).slice(0, 12));
      })
      .catch(() => !cancelled && setPartResults([]))
      .finally(() => !cancelled && setPartLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debouncedPart, draft.carMaker]);

  const supplierMeta = useMemo(() => {
    const autonomous = env?.autonomousSupplierIds || [];
    const pc = env?.partConfirmationSupplierIds || [];
    return (s: TestSupplierOption) => ({
      isLean: !autonomous.includes(s._id),
      partConfirmation: pc.includes(s._id),
    });
  }, [env]);

  const set = <K extends keyof TesterConfig>(key: K, value: TesterConfig[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const pickCustomer = (customer: TestCustomerOption) => {
    setDraft((d) => applyCustomerToConfig({ ...d, shipmentAddressId: undefined }, customer, env?.prefill));
    setCustomerQuery("");
    setCustomerSearchHits([]);
  };

  const pickAddress = (addressId: string) => {
    if (!customerDetail) return;
    setDraft((d) => applyCustomerToConfig({ ...d, shipmentAddressId: addressId }, customerDetail, env?.prefill));
  };

  const pickSupplier = (supplierId: string) => {
    const supplier = env?.testSuppliers.find((s) => s._id === supplierId);
    if (!supplier) return;
    if (!supplier.isTestSupplier) {
      toast({ status: "error", description: "Only test suppliers can be selected." });
      return;
    }
    setDraft((d) => applySupplierToConfig(d, supplier));
  };

  const addPart = (hit: NonNullable<ReturnType<typeof mapPartSearchHit>>) => {
    setDraft((d) => {
      if (d.parts.some((p) => p.partId === hit._id)) return d;
      const part: TesterPart = {
        partId: hit._id,
        partNumber: hit.partNumber,
        partName: hit.partName,
        rootPartNumber: hit.rootPartNumber,
        mrp: hit.mrp,
        quantity: 1,
      };
      return { ...d, parts: [...d.parts, part] };
    });
    setPartQuery("");
    setPartResults([]);
  };

  const updatePartQty = (partId: string, qty: number) =>
    setDraft((d) => ({
      ...d,
      parts: d.parts.map((p) => (p.partId === partId ? { ...p, quantity: Math.max(1, qty || 1) } : p)),
    }));

  const removePart = (partId: string) =>
    setDraft((d) => ({ ...d, parts: d.parts.filter((p) => p.partId !== partId) }));

  const onUpload = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(Array.from(files).map((f) => Services.uploadMedia(f)));
      const ids = uploaded.flat().map((m) => String(m._id));
      setDraft((d) => ({ ...d, orderMediaIds: [...d.orderMediaIds, ...ids] }));
      toast({ status: "success", description: `${ids.length} file(s) uploaded` });
    } catch (err: any) {
      toast({ status: "error", description: err?.message || "Upload failed" });
    } finally {
      setUploading(false);
    }
  };

  const validationErrors = useMemo(() => {
    const errors: string[] = [];
    if (!draft.customerId) errors.push("Select a customer");
    if (!draft.supplierId) errors.push("Select a test supplier");
    else if (draft.isTestSupplier === false) errors.push("Selected supplier is not a test supplier");
    if (!draft.parts.length) errors.push("Add at least one part");
    if (!draft.shipmentAddress) errors.push("Customer has no shipment address");
    if (draft.dispatchMode === "SELF_DISPATCH" && !draft.rider.id) errors.push("Select a saved rider");
    if (draft.pollMaxAttempts < 1) errors.push("Polling attempts must be ≥ 1");
    return errors;
  }, [draft]);

  const save = () => {
    if (validationErrors.length) {
      toast({ status: "error", description: validationErrors.join(". ") });
      return;
    }
    onSave(draft);
    onClose();
  };

  const addresses: any[] = customerDetail?.addresses || [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent>
        <ModalHeader>
          <HStack justify="space-between" pr={8}>
            <Text>Edit test configuration</Text>
            <Badge colorScheme="orange" variant="subtle">
              test suppliers
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs variant="enclosed-colored" colorScheme="blue" size="sm">
            <TabList>
              <Tab>Customer & address</Tab>
              <Tab>Supplier</Tab>
              <Tab>Parts & media</Tab>
              <Tab>Order details</Tab>
              <Tab>Fulfilment & polling</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <VStack align="stretch" spacing={5}>
                  <Section
                    title="Customer"
                    hint={
                      showAllCustomers
                        ? "Search by name, code or mobile among all customers. A non-test customer places a normal order."
                        : "Search by name, code or mobile among test customers. Tick “Show all customers” to include non-test customers."
                    }
                    action={
                      <Checkbox
                        size="sm"
                        isChecked={showAllCustomers}
                        onChange={(e) => setShowAllCustomers(e.target.checked)}
                      >
                        Show all customers
                      </Checkbox>
                    }
                  >
                    <Input
                      placeholder="Search customers…"
                      value={customerQuery}
                      onChange={(e) => setCustomerQuery(e.target.value)}
                      bg="white"
                    />
                    {(customerLoading || customerResults.length > 0) && (
                      <Box mt={2} borderWidth="1px" borderRadius="md" bg="white" maxH="200px" overflowY="auto">
                        {customerLoading ? (
                          <HStack p={3}>
                            <Spinner size="sm" />
                            <Text fontSize="sm">Searching…</Text>
                          </HStack>
                        ) : (
                          <List>
                            {customerResults.map((c) => (
                              <ListItem
                                key={c._id}
                                px={3}
                                py={2}
                                cursor="pointer"
                                _hover={{ bg: "blue.50" }}
                                onClick={() => pickCustomer(c)}
                              >
                                <HStack justify="space-between">
                                  <Box>
                                    <Text fontSize="sm" fontWeight="600">
                                      {c.name}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      {c.code} · {c.mobileNumber}
                                    </Text>
                                  </Box>
                                  {c.isTestCustomer ? (
                                    <Badge colorScheme="orange">TEST</Badge>
                                  ) : null}
                                </HStack>
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </Box>
                    )}
                    {!customerLoading && debouncedCustomer.trim().length >= 2 && customerResults.length === 0 && (
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        {hiddenCustomerCount > 0
                          ? `No test customers match “${debouncedCustomer}”. ${hiddenCustomerCount} non-test customer(s) hidden — tick “Show all customers”.`
                          : `No customers match “${debouncedCustomer}”.`}
                      </Text>
                    )}
                    <Box mt={3} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                      {draft.customerId ? (
                        <HStack justify="space-between">
                          <Box>
                            <Text fontSize="sm" fontWeight="600">
                              {draft.customerName || draft.customerId}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {draft.customerCode} · {draft.customerMobile} · {draft.customerId}
                            </Text>
                          </Box>
                          {draft.isTestCustomer !== false ? (
                            <Badge colorScheme="orange">TEST</Badge>
                          ) : (
                            <Badge colorScheme="gray">Customer</Badge>
                          )}
                        </HStack>
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          No customer selected
                        </Text>
                      )}
                    </Box>
                  </Section>

                  <Section title="Shipment address" hint="Pick one of the customer's saved addresses.">
                    {addresses.length ? (
                      <Select
                        bg="white"
                        value={draft.shipmentAddressId || ""}
                        onChange={(e) => pickAddress(e.target.value)}
                      >
                        {addresses.map((a: any) => (
                          <option key={String(a._id)} value={String(a._id)}>
                            {[a.addressLine1, a.addressLine2, a.city, a.state, a.pinCode].filter(Boolean).join(", ")}
                            {a.isDefault ? " (default)" : ""}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        {draft.shipmentAddress
                          ? `Using configured address: ${(draft.shipmentAddress as any).addressLine1}, ${
                              (draft.shipmentAddress as any).city
                            }`
                          : "Customer has no saved addresses."}
                      </Text>
                    )}
                  </Section>

                  <Section title="Billing details" hint="Derived from the customer; adjust the billed name / GST if needed.">
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="xs">Billing name</FormLabel>
                          <Input
                            size="sm"
                            bg="white"
                            value={String((draft.billingDetails as any)?.name ?? "")}
                            onChange={(e) =>
                              set("billingDetails", { ...(draft.billingDetails || {}), name: e.target.value })
                            }
                          />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="xs">GST</FormLabel>
                          <Input
                            size="sm"
                            bg="white"
                            value={String((draft.billingDetails as any)?.gst ?? "")}
                            onChange={(e) =>
                              set("billingDetails", { ...(draft.billingDetails || {}), gst: e.target.value })
                            }
                          />
                        </FormControl>
                      </GridItem>
                    </Grid>
                  </Section>
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <Section
                  title="Test supplier"
                  hint="Only suppliers flagged isTestSupplier=true are listed. Lean suppliers go through Garaaz review (REVIEW → IN_REVIEW → READY); autonomous suppliers mark READY directly."
                >
                  {env?.testSuppliers.length ? (
                    <VStack align="stretch" spacing={2}>
                      {env.testSuppliers.map((s) => {
                        const meta = supplierMeta(s);
                        const selected = s._id === draft.supplierId;
                        return (
                          <Box
                            key={s._id}
                            p={3}
                            borderWidth="1px"
                            borderRadius="md"
                            cursor="pointer"
                            bg={selected ? "blue.50" : "white"}
                            borderColor={selected ? "blue.300" : "gray.200"}
                            onClick={() => pickSupplier(s._id)}
                            transition="all 0.15s"
                          >
                            <HStack justify="space-between">
                              <Box>
                                <Text fontSize="sm" fontWeight="600">
                                  {s.name}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {s._id} · {s.mobileNumber || "no mobile on user"}
                                </Text>
                              </Box>
                              <HStack>
                                <Badge colorScheme="orange">TEST</Badge>
                                <Badge colorScheme={meta.isLean ? "purple" : "green"}>
                                  {meta.isLean ? "lean review" : "autonomous"}
                                </Badge>
                                {meta.partConfirmation && <Badge colorScheme="teal">part confirmation</Badge>}
                              </HStack>
                            </HStack>
                          </Box>
                        );
                      })}
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color="red.500">
                      No test suppliers available in this environment.
                    </Text>
                  )}
                </Section>
              </TabPanel>

              <TabPanel px={0}>
                <VStack align="stretch" spacing={5}>
                  <Section title="Parts" hint="Search part master and add rows; quantities are used for the enquiry and supplier fulfilment.">
                    <Input
                      placeholder="Search parts by number or name…"
                      value={partQuery}
                      onChange={(e) => setPartQuery(e.target.value)}
                      bg="white"
                    />
                    {(partLoading || partResults.length > 0) && (
                      <Box mt={2} borderWidth="1px" borderRadius="md" bg="white" maxH="220px" overflowY="auto">
                        {partLoading ? (
                          <HStack p={3}>
                            <Spinner size="sm" />
                            <Text fontSize="sm">Searching…</Text>
                          </HStack>
                        ) : (
                          <List>
                            {partResults.map((hit) =>
                              hit ? (
                                <ListItem
                                  key={hit._id}
                                  px={3}
                                  py={2}
                                  cursor="pointer"
                                  _hover={{ bg: "blue.50" }}
                                  onClick={() => addPart(hit)}
                                >
                                  <Text fontSize="sm" fontWeight="600">
                                    {hit.partNumber}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {hit.partName} · {hit.brandName} · ₹{hit.mrp ?? "–"}
                                  </Text>
                                </ListItem>
                              ) : null
                            )}
                          </List>
                        )}
                      </Box>
                    )}
                    <VStack align="stretch" spacing={2} mt={3}>
                      {draft.parts.map((p) => (
                        <HStack key={p.partId} p={2} borderWidth="1px" borderRadius="md" bg="gray.50">
                          <Box flex={1}>
                            <Text fontSize="sm" fontWeight="600">
                              {p.partNumber || p.partId}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {p.partName} {p.mrp ? `· ₹${p.mrp}` : ""}
                            </Text>
                          </Box>
                          <NumberInput
                            size="sm"
                            w="90px"
                            min={1}
                            value={p.quantity}
                            onChange={(_, v) => updatePartQty(p.partId, v)}
                          >
                            <NumberInputField bg="white" />
                          </NumberInput>
                          <IconButton
                            aria-label="Remove part"
                            icon={<CloseIcon boxSize={2.5} />}
                            size="xs"
                            variant="ghost"
                            onClick={() => removePart(p.partId)}
                          />
                        </HStack>
                      ))}
                      {!draft.parts.length && (
                        <Text fontSize="sm" color="red.500">
                          Add at least one part.
                        </Text>
                      )}
                    </VStack>
                  </Section>

                  <Section title="Order media" hint="Optional attachments for the enquiry (uploaded via POST /media).">
                    <HStack>
                      <Input type="file" multiple size="sm" bg="white" onChange={(e) => onUpload(e.target.files)} isDisabled={uploading} />
                      {uploading && <Spinner size="sm" />}
                    </HStack>
                    <HStack mt={2} wrap="wrap" spacing={2}>
                      {draft.orderMediaIds.map((id) => (
                        <Tag key={id} size="sm" colorScheme="gray">
                          {id}
                          <IconButton
                            aria-label="remove"
                            icon={<CloseIcon boxSize={2} />}
                            size="xs"
                            variant="ghost"
                            ml={1}
                            onClick={() => set("orderMediaIds", draft.orderMediaIds.filter((m) => m !== id))}
                          />
                        </Tag>
                      ))}
                    </HStack>
                  </Section>
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <VStack align="stretch" spacing={4}>
                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <FormLabel fontSize="sm" mb={0}>
                        Require Price Estimate
                      </FormLabel>
                      <Text fontSize="xs" color="gray.500">
                        On: draft estimate shared after part conversion, then the customer responds to the draft estimate. Off: existing flow.
                      </Text>
                    </Box>
                    <Switch
                      colorScheme="purple"
                      isChecked={Boolean(draft.requirePriceEstimate)}
                      onChange={(e) => set("requirePriceEstimate", e.target.checked)}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Customer remarks</FormLabel>
                    <Textarea bg="white" size="sm" value={draft.remarks} onChange={(e) => set("remarks", e.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Car maker</FormLabel>
                    {carMakers.length ? (
                      <Select bg="white" size="sm" value={draft.carMaker} onChange={(e) => set("carMaker", e.target.value)}>
                        <option value="">— none —</option>
                        {carMakers.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <Input bg="white" size="sm" value={draft.carMaker} onChange={(e) => set("carMaker", e.target.value)} />
                    )}
                  </FormControl>
                  <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">Transaction type</FormLabel>
                      <Select bg="white" size="sm" value={draft.transactionType} onChange={(e) => set("transactionType", e.target.value as any)}>
                        <option value="Facilitation">Facilitation</option>
                        <option value="Trading">Trading</option>
                      </Select>
                      <FormHelperText fontSize="xs">Trading requires the Garaaz invoice event before R2D.</FormHelperText>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Payable by</FormLabel>
                      <Select bg="white" size="sm" value={draft.payableBy} onChange={(e) => set("payableBy", e.target.value)}>
                        <option value="CUSTOMER">CUSTOMER</option>
                        <option value="INSURANCE">INSURANCE</option>
                        <option value="NBFC">NBFC</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Payable to</FormLabel>
                      <Select bg="white" size="sm" value={draft.payableTo} onChange={(e) => set("payableTo", e.target.value)}>
                        <option value="SUPPLIER">SUPPLIER</option>
                        <option value="GARAAZ">GARAAZ</option>
                      </Select>
                    </FormControl>
                  </Grid>
                  <FormControl>
                    <FormLabel fontSize="sm">Preferred delivery option</FormLabel>
                    <Select
                      bg="white"
                      size="sm"
                      value={draft.preferredDelivery || "STANDARD"}
                      onChange={(e) => set("preferredDelivery", e.target.value as any)}
                    >
                      <option value="STANDARD">Standard Delivery (default)</option>
                      <option value="INSTANT">Instant Delivery</option>
                      <option value="SELF_PICKUP">Self Pickup</option>
                    </Select>
                    <FormHelperText fontSize="xs">
                      Picked from the estimate&apos;s delivery options (CHEAPEST / Standard first). If that slab is missing, a manual delivery option is added for it (CPT 2–7 days out, ₹50–250; Self Pickup ₹0).
                    </FormHelperText>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Cancellation reason (for cancel branches)</FormLabel>
                    <Input bg="white" size="sm" value={draft.cancellationReason} onChange={(e) => set("cancellationReason", e.target.value)} />
                  </FormControl>
                </VStack>
              </TabPanel>

              <TabPanel px={0}>
                <VStack align="stretch" spacing={5}>
                  <Section title="Supplier invoice defaults">
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">Invoice no. prefix</FormLabel>
                        <Input bg="white" size="sm" maxLength={6} value={draft.invoice.invoiceNoPrefix} onChange={(e) => set("invoice", { ...draft.invoice, invoiceNoPrefix: e.target.value })} />
                        <FormHelperText fontSize="xs">A unique suffix is appended per run (max 16 chars total).</FormHelperText>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Invoice amount</FormLabel>
                        <Input bg="white" size="sm" value={draft.invoice.amount} onChange={(e) => set("invoice", { ...draft.invoice, amount: e.target.value })} />
                      </FormControl>
                    </Grid>
                  </Section>
                  <Divider />
                  <Section title="Dispatch" hint="SYSTEM_PORTER is intentionally unavailable so no real courier is ever booked.">
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">Dispatch mode</FormLabel>
                        <Select bg="white" size="sm" value={draft.dispatchMode} onChange={(e) => set("dispatchMode", e.target.value as any)}>
                          <option value="SELF_DISPATCH">SELF_DISPATCH (rider)</option>
                          <option value="SELF_PICKUP">SELF_PICKUP</option>
                        </Select>
                      </FormControl>
                    </Grid>
                    <FormControl id="saved-rider" mt={3}>
                      <FormLabel fontSize="sm">Select saved rider</FormLabel>
                      <Select
                        bg="white"
                        size="sm"
                        placeholder="Select rider"
                        value={draft.rider.id || ""}
                        onChange={(e) => {
                          const rider = savedRiders.find((r) => r.id === e.target.value);
                          if (!rider) return;
                          set("rider", {
                            ...draft.rider,
                            id: rider.id,
                            name: rider.name,
                            phoneNumber: rider.phone,
                          });
                        }}
                      >
                        {savedRiders.map((r) => (
                          <option key={r.id} value={r.id}>{`${r.name} (${r.phone})`}</option>
                        ))}
                      </Select>
                    </FormControl>
                    <Grid templateColumns="repeat(3, 1fr)" gap={3} mt={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">Rider name</FormLabel>
                        <Input bg="white" size="sm" value={draft.rider.name} isReadOnly isDisabled placeholder="Select rider to populate" />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Rider phone</FormLabel>
                        <Input bg="white" size="sm" value={draft.rider.phoneNumber} isReadOnly isDisabled placeholder="Select rider to populate" />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Vehicle number</FormLabel>
                        <Input bg="white" size="sm" value={draft.rider.vehicleNumber} onChange={(e) => set("rider", { ...draft.rider, vehicleNumber: e.target.value })} />
                      </FormControl>
                    </Grid>
                  </Section>
                  <Divider />
                  <Section title="Events & polling" hint="Polling uses 2s → 3s → 5s intervals between checks.">
                    <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">Max polling attempts</FormLabel>
                        <NumberInput size="sm" min={1} max={60} value={draft.pollMaxAttempts} onChange={(_, v) => set("pollMaxAttempts", Number.isFinite(v) ? v : DEFAULT_TESTER_CONFIG.pollMaxAttempts)}>
                          <NumberInputField bg="white" />
                        </NumberInput>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Garaaz invoice event</FormLabel>
                        <Select bg="white" size="sm" value={draft.invoiceEventPolicy} onChange={(e) => set("invoiceEventPolicy", e.target.value as any)}>
                          <option value="ask">Ask each run</option>
                          <option value="wait">Always wait</option>
                          <option value="skip">Skip (Facilitation only)</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Order DELIVERED event</FormLabel>
                        <Select bg="white" size="sm" value={draft.deliveredEventPolicy} onChange={(e) => set("deliveredEventPolicy", e.target.value as any)}>
                          <option value="ask">Wait, ask on timeout</option>
                          <option value="wait">Wait, ask on timeout</option>
                          <option value="skip">Use DELIVER transition directly</option>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Section>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <HStack w="100%" justify="space-between">
            <Text fontSize="xs" color={validationErrors.length ? "red.500" : "gray.500"}>
              {validationErrors.length ? validationErrors.join(" · ") : "Configuration is valid"}
            </Text>
            <HStack>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={save} isDisabled={validationErrors.length > 0}>
                Save configuration
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EditConfigModal;
